/**
 * Pagination.
 *
 * The old bar was Previous / Next and a sentence. On a 12-page list that is
 * eleven clicks to reach the end and no way to say "show me 100 at a time",
 * which is why every admin was paging through 240 users 20 at a time. This
 * adds a page-size control, numbered pages with ellipses, and first/last jumps.
 */
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { PAGE_SIZES } from '@/hooks/use-url-state';
import { cn } from '@/lib/utils';

/**
 * Which page numbers to render. Always shows first, last, current and its
 * neighbours; everything else collapses to an ellipsis, so the control is a
 * fixed width whether there are 3 pages or 300.
 */
export function page_window(current: number, total: number, span = 1): (number | 'gap')[] {
  if (total <= 1) return [1];
  const pages = new Set<number>([1, total]);
  for (let p = current - span; p <= current + span; p++) {
    if (p >= 1 && p <= total) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push('gap');
    out.push(p);
    prev = p;
  }
  return out;
}

const nav_btn =
  'inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-control px-2 text-data font-semibold transition-colors disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function PaginationBar({
  page,
  page_size,
  total,
  is_fetching,
  on_page_change,
  on_page_size_change,
  className,
}: {
  page: number;
  page_size: number;
  total: number;
  is_fetching?: boolean;
  on_page_change: (next: number) => void;
  on_page_size_change?: (next: number) => void;
  className?: string;
}) {
  const total_pages = total > 0 ? Math.ceil(total / page_size) : 1;
  const first = total === 0 ? 0 : (page - 1) * page_size + 1;
  const last = Math.min(page * page_size, total);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2',
        className,
      )}
      data-testid="pagination"
    >
      <div className="flex items-center gap-3">
        {/* "1–20 of 240" beats "page 1 of 12": it answers "how far in am I"
            without the reader multiplying anything. */}
        <p className="text-data text-muted-foreground">
          <span className="tabular font-semibold text-foreground">
            {first.toLocaleString('en-IN')}–{last.toLocaleString('en-IN')}
          </span>{' '}
          of <span className="tabular font-semibold text-foreground">{total.toLocaleString('en-IN')}</span>
        </p>
        {on_page_size_change && (
          <label className="flex items-center gap-1.5 text-data text-muted-foreground">
            <span className="hidden sm:inline">Rows</span>
            <select
              className="console-select !h-8 !text-data"
              value={page_size}
              onChange={(e) => on_page_size_change(Number(e.target.value))}
              data-testid="page-size"
              aria-label="Rows per page"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <nav className="flex items-center gap-1" aria-label="Pagination">
        <button
          type="button"
          className={cn(nav_btn, 'hover:bg-muted')}
          disabled={page <= 1 || is_fetching}
          onClick={() => on_page_change(1)}
          aria-label="First page"
          data-testid="page-first"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={cn(nav_btn, 'hover:bg-muted')}
          disabled={page <= 1 || is_fetching}
          onClick={() => on_page_change(Math.max(1, page - 1))}
          aria-label="Previous page"
          data-testid="page-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="hidden items-center gap-1 sm:flex">
          {page_window(page, total_pages).map((p, i) =>
            p === 'gap' ? (
              <span key={`gap-${i}`} className="px-1 text-data text-muted-foreground">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={cn(
                  nav_btn,
                  'tabular',
                  p === page
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
                aria-current={p === page ? 'page' : undefined}
                disabled={is_fetching}
                onClick={() => on_page_change(p)}
                data-testid={`page-${p}`}
              >
                {p}
              </button>
            ),
          )}
        </div>
        {/* Narrow screens get the count instead of the number strip. */}
        <span className="px-2 text-data text-muted-foreground tabular sm:hidden">
          {page} / {total_pages}
        </span>

        <button
          type="button"
          className={cn(nav_btn, 'hover:bg-muted')}
          disabled={page >= total_pages || is_fetching}
          onClick={() => on_page_change(page + 1)}
          aria-label="Next page"
          data-testid="page-next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={cn(nav_btn, 'hover:bg-muted')}
          disabled={page >= total_pages || is_fetching}
          onClick={() => on_page_change(total_pages)}
          aria-label="Last page"
          data-testid="page-last"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </nav>
    </div>
  );
}
